import { useEffect, useState } from 'react';
import type { OperationToQuickloadUrl } from '../operation-to-quickload-url.generated';
import { cacheFactory } from '../cacheFactory';
import { getPreloadsFromPath } from '../getPreloadsFromPath';
import { getValidPreloadsForRoute } from './getValidPreloadsForRoute';
import type { DocumentNode } from 'graphql';

type Operations = (keyof typeof OperationToQuickloadUrl)[];

export const useQuickLoad = ({
  waitOn,
  document,
  skip,
}: {
  waitOn: Operations;
  document: DocumentNode;
  skip?: boolean;
}) => {
  const validPreloadsForInitialPath = getValidPreloadsForRoute();
  const validPreloadsForCurrentPath = getPreloadsFromPath().preloads.map(
    ({ queryName }) => queryName,
  );
  const isValidOperationToWaitOn = waitOn.every((operation) =>
    validPreloadsForInitialPath.includes(operation),
  );
  const isOnInitialPath =
    validPreloadsForInitialPath.sort().join() ===
    validPreloadsForCurrentPath.sort().join();
  const canUseWaitOn =
    // dev didn't supply waitOn argument
    waitOn.length > 0 &&
    // dev specified None, which means skip all this
    waitOn[0] !== 'None' &&
    // dev attempted to wait on preload that isn't valid for the initial route
    isValidOperationToWaitOn &&
    // we don't support route transitions since requests aren't made through QuickLoad anymore
    isOnInitialPath;
  const canQuickLoad = canUseWaitOn && !skip;
  const [isQuickLoading, setIsQuickLoading] = useState(
    canQuickLoad ? true : false,
  );

  useEffect(() => {
    if (!canQuickLoad) {
      if (isQuickLoading) {
        setIsQuickLoading(false);
      }
      return;
    }

    const start = async () => {
      for (const operation of waitOn) {
        await cacheFactory.waitForQueryHydratedTo(operation, 'Apollo');
      }
      setIsQuickLoading(false);
    };

    start();
    // intentionally disabled since we don't support changing
    // the operations that you are waiting on
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [skip]);

  return isQuickLoading;
};
